import { useEffect, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { saveFiles, getFiles, removeFiles } from '@/shared/utils/indexedDB';
import { OfferFormData } from '../types/offer.types';

const DRAFT_STORAGE_KEY = 'publish_offer_draft';
const DRAFT_TIMESTAMP_KEY = 'publish_offer_draft_timestamp';
const DRAFT_PHOTOS_KEY = 'publish_offer_photos';
const DRAFT_PRODUCT_COUNT_KEY = 'publish_offer_product_count';
const DRAFT_PRODUCT_PHOTOS_KEY_PREFIX = 'publish_offer_product_photos_';
const DRAFT_DOCUMENTS_KEY = 'publish_offer_documents';

interface SerializableFormData {
  title: string;
  description: string;
  address: string;
  products: Array<{
    materialId: string;
    materialCategory: string;
    materialSubcategory: string;
    unitPrice: string;
    quantity: string;
    unitId: string;
    condition: 'new' | 'used';
  }>;
  startDate: string | null;
  endDate: string | null;
  // Note: File objects (photos, documents) are stored separately in IndexedDB
}

interface UsePersistentFormOptions {
  restoreOnMount?: boolean;
}

/**
 * Custom hook for persisting offer form data to localStorage and IndexedDB.
 * Automatically saves form changes (debounced) and restores on mount.
 *
 * - Text fields and dates are stored in localStorage
 * - File objects (photos, documents) are stored in IndexedDB
 */
export function usePersistentForm(
  methods: UseFormReturn<OfferFormData>,
  { restoreOnMount = true }: UsePersistentFormOptions = {}
) {
  const formValues = methods.watch();
  const isRestoringRef = useRef(true);
  const isDisabledRef = useRef(false);

  // Debounce form values to avoid excessive localStorage writes
  const debouncedValues = useDebounce(formValues, 500);

  // Save form data to localStorage (debounced)
  useEffect(() => {
    if (isRestoringRef.current || isDisabledRef.current) {
      return;
    }
    try {
      // Serialize form data (excluding File objects)
      const serializableData: SerializableFormData = {
        title: debouncedValues.title || '',
        description: debouncedValues.description || '',
        address: debouncedValues.address || '',
        products: (debouncedValues.products || []).map(product => ({
          materialId: product.materialId || '',
          materialCategory: product.materialCategory || '',
          materialSubcategory: product.materialSubcategory || '',
          unitPrice: product.unitPrice || '',
          quantity: product.quantity || '',
          unitId: product.unitId || '',
          condition: product.condition || 'used',
        })),
        startDate: debouncedValues.startDate ? debouncedValues.startDate.toISOString() : null,
        endDate: debouncedValues.endDate ? debouncedValues.endDate.toISOString() : null,
      };

      // Only save if there's meaningful data (not all empty)
      const hasProductData = serializableData.products.some(product =>
        Object.entries(product).some(([key, value]) => {
          if (key === 'condition') return false;
          return value !== '' && value !== null;
        })
      );
      const hasData =
        hasProductData ||
        Object.entries(serializableData).some(([key, value]) => {
          if (key === 'products') return false;
          if (key === 'startDate') return false;
          return value !== '' && value !== null;
        });

      if (hasData) {
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(serializableData));
        localStorage.setItem(DRAFT_TIMESTAMP_KEY, new Date().toISOString());
      }

      const productCount = debouncedValues.products?.length ?? 0;
      const previousCount = Number.parseInt(
        localStorage.getItem(DRAFT_PRODUCT_COUNT_KEY) || '0',
        10
      );
      if (previousCount > productCount) {
        for (let i = productCount; i < previousCount; i += 1) {
          removeFiles(`${DRAFT_PRODUCT_PHOTOS_KEY_PREFIX}${i}`).catch(error => {
            console.error('Error removing photos from IndexedDB:', error);
          });
        }
      }
      localStorage.setItem(DRAFT_PRODUCT_COUNT_KEY, productCount.toString());

      // Save photos per product to IndexedDB
      if (debouncedValues.products && debouncedValues.products.length > 0) {
        debouncedValues.products.forEach((product, index) => {
          if (product.photos && product.photos.length > 0) {
            saveFiles(`${DRAFT_PRODUCT_PHOTOS_KEY_PREFIX}${index}`, product.photos).catch(error => {
              console.error('Error saving photos to IndexedDB:', error);
            });
          } else {
            removeFiles(`${DRAFT_PRODUCT_PHOTOS_KEY_PREFIX}${index}`).catch(error => {
              console.error('Error removing photos from IndexedDB:', error);
            });
          }
        });
      }

      if (debouncedValues.documents && debouncedValues.documents.length > 0) {
        saveFiles(DRAFT_DOCUMENTS_KEY, debouncedValues.documents).catch(error => {
          console.error('Error saving documents to IndexedDB:', error);
        });
      }
    } catch (error) {
      console.error('Error saving form draft to localStorage:', error);
    }
  }, [debouncedValues]);

  // Restore form data from localStorage on mount
  useEffect(() => {
    if (!restoreOnMount) {
      isRestoringRef.current = false;
      return;
    }

    const restoreFormData = async () => {
      try {
        const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);

        if (savedDraft) {
          const parsedDraft = JSON.parse(savedDraft) as SerializableFormData &
            Partial<{
              materialId: string;
              materialCategory: string;
              materialSubcategory: string;
              unitPrice: string;
              quantity: string;
              unitId: string;
              condition: 'new' | 'used';
            }>;

          // Restore each field using setValue with validation
          if (parsedDraft.title) {
            methods.setValue('title', parsedDraft.title, { shouldValidate: true });
          }
          if (parsedDraft.description !== undefined) {
            methods.setValue('description', parsedDraft.description, { shouldValidate: true });
          }
          if (parsedDraft.address) {
            methods.setValue('address', parsedDraft.address, { shouldValidate: true });
          }
          if (parsedDraft.products && parsedDraft.products.length > 0) {
            methods.setValue(
              'products',
              parsedDraft.products.map(product => ({
                ...product,
                photos: [],
              })),
              { shouldValidate: true }
            );
          } else if (
            parsedDraft.materialId ||
            parsedDraft.materialCategory ||
            parsedDraft.materialSubcategory ||
            parsedDraft.unitPrice ||
            parsedDraft.quantity ||
            parsedDraft.unitId
          ) {
            methods.setValue(
              'products',
              [
                {
                  materialId: parsedDraft.materialId || '',
                  materialCategory: parsedDraft.materialCategory || '',
                  materialSubcategory: parsedDraft.materialSubcategory || '',
                  unitPrice: parsedDraft.unitPrice || '',
                  quantity: parsedDraft.quantity || '',
                  unitId: parsedDraft.unitId || '',
                  condition: parsedDraft.condition || 'used',
                  photos: [],
                },
              ],
              { shouldValidate: true }
            );
          }
          if (parsedDraft.startDate) {
            methods.setValue('startDate', new Date(parsedDraft.startDate), {
              shouldValidate: true,
            });
          }
          if (parsedDraft.endDate) {
            methods.setValue('endDate', new Date(parsedDraft.endDate), { shouldValidate: true });
          }
        }

        const savedProductsCount = Number.parseInt(
          localStorage.getItem(DRAFT_PRODUCT_COUNT_KEY) || '0',
          10
        );

        for (let i = 0; i < savedProductsCount; i += 1) {
          const savedPhotos = await getFiles(`${DRAFT_PRODUCT_PHOTOS_KEY_PREFIX}${i}`);
          if (savedPhotos.length > 0) {
            methods.setValue(`products.${i}.photos`, savedPhotos, { shouldValidate: true });
          }
        }

        // Backward compatibility: restore legacy single photos key into first product
        const legacyPhotos = await getFiles(DRAFT_PHOTOS_KEY);
        if (legacyPhotos.length > 0) {
          methods.setValue('products.0.photos', legacyPhotos, { shouldValidate: true });
          await removeFiles(DRAFT_PHOTOS_KEY);
        }

        // Restore documents from IndexedDB
        const savedDocuments = await getFiles(DRAFT_DOCUMENTS_KEY);
        if (savedDocuments.length > 0) {
          methods.setValue('documents', savedDocuments, { shouldValidate: true });
        }
      } catch (error) {
        console.error('Error restoring form draft from localStorage:', error);
      } finally {
        isRestoringRef.current = false;
      }
    };

    restoreFormData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoreOnMount]); // Only run on mount (or restore option change)

  /**
   * Clears the saved draft from localStorage and IndexedDB.
   * Should be called after successful form submission.
   */
  const clearDraft = async () => {
    // Disable persistence to prevent debounced saves from re-writing cleared data
    isDisabledRef.current = true;
    try {
      // Clear localStorage
      localStorage.removeItem(DRAFT_STORAGE_KEY);
      localStorage.removeItem(DRAFT_TIMESTAMP_KEY);
      const productCount = Number.parseInt(
        localStorage.getItem(DRAFT_PRODUCT_COUNT_KEY) || '0',
        10
      );
      localStorage.removeItem(DRAFT_PRODUCT_COUNT_KEY);

      // Clear IndexedDB files
      const productPhotoRemovals = Array.from({ length: productCount }, (_v, index) =>
        removeFiles(`${DRAFT_PRODUCT_PHOTOS_KEY_PREFIX}${index}`)
      );
      await Promise.all([
        removeFiles(DRAFT_PHOTOS_KEY),
        removeFiles(DRAFT_DOCUMENTS_KEY),
        ...productPhotoRemovals,
      ]);
    } catch (error) {
      console.error('Error clearing form draft:', error);
    }
  };

  return { clearDraft };
}
